import React, { useState } from 'react';
import '../App.css';
import {
  getGuidelinesMarketBands,
  MULTI_DAY_COMPLIMENTARY_NOTE,
} from '../pricing/markets';

/** Stroke icons matched to sidebar weight (24×24, stroke 1.5). */
function ImpactIcon({ paths }) {
  return (
    <svg
      className="guidelines-impact-icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

const IMPACT_FACTORS = [
  {
    title: 'Market',
    body: 'Delivery standards and minimums vary by location.',
    icon: [
      'M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z',
      'M12 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
    ],
  },
  {
    title: 'Rental Value',
    body: 'Rental value determines the applicable pricing tier and Complimentary Delivery Threshold.',
    icon: [
      'M12 2v20',
      'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    ],
  },
  {
    title: 'Timing',
    body: 'Extended, restricted, after-hours, or multi-day schedules may increase delivery cost.',
    icon: [
      'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
      'M12 6v6l4 2',
    ],
  },
  {
    title: 'Access',
    body: 'Stairs, long pushes, freight elevators, and limited loading areas can increase labor and time.',
    icon: [
      'M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16',
      'M4 21h16',
      'M14 21v-7h4v7',
      'M10 10h.01',
    ],
  },
  {
    title: 'Travel',
    body: 'Long-distance projects may require additional drive time, crew time, lodging, or transportation expenses.',
    icon: [
      'M1 7h13v10H1z',
      'M14 10h4l3 3v4h-7V10z',
      'M5 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
      'M17.5 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
    ],
  },
  {
    title: 'Scope',
    body: 'Additional trucks, crew, venues, or major scope changes can affect the final delivery fee.',
    icon: [
      'M12 2 2 7l10 5 10-5-10-5z',
      'M2 12l10 5 10-5',
      'M2 17l10 5 10-5',
    ],
  },
];

const MARKET_BANDS = getGuidelinesMarketBands();

const NON_STANDARD_ITEMS = [
  'Multi-day installation or strike',
  'Extended crew hours',
  'Difficult site access',
  'Restricted or off-hours load-in/load-out',
  'Additional trucks, crew, or delivery time',
  'Multiple venues or unusually complex logistics',
];

function DeliveryGuidelines() {
  const [marketsOpen, setMarketsOpen] = useState(true);
  const [openMarketIds, setOpenMarketIds] = useState(() => new Set());

  const toggleMarket = (id) => {
    setOpenMarketIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="guidelines-container">
      <div className="guidelines-content">
        <section className="guidelines-section guidelines-section--intro">
          <p className="guidelines-intro">
            Delivery pricing is influenced by the project’s market, rental value, timing, access,
            travel requirements, and overall scope. The Delivery Fee Calculator applies the
            standard pricing structure. These guidelines provide context for how delivery is
            priced and when additional fees may apply.
          </p>
        </section>

        <section className="guidelines-section" aria-labelledby="guidelines-impacts">
          <h3 id="guidelines-impacts" className="guidelines-label">
            What Impacts Delivery Cost
          </h3>
          <ul className="guidelines-impact-grid">
            {IMPACT_FACTORS.map((factor) => (
              <li key={factor.title} className="guidelines-impact-item">
                <div className="guidelines-impact-head">
                  <ImpactIcon paths={factor.icon} />
                  <span className="guidelines-impact-title">{factor.title}</span>
                </div>
                <p className="guidelines-impact-body">{factor.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="guidelines-section guidelines-section--markets" aria-labelledby="guidelines-markets">
          <button
            type="button"
            className="guidelines-disclosure"
            aria-expanded={marketsOpen}
            aria-controls="guidelines-markets-panel"
            onClick={() => setMarketsOpen((open) => !open)}
          >
            <h3 id="guidelines-markets" className="guidelines-label guidelines-label--inline">
              Standard Delivery by Market
            </h3>
            <span className="guidelines-disclosure-mark" aria-hidden="true">
              {marketsOpen ? '−' : '+'}
            </span>
          </button>

          <p className="guidelines-text guidelines-text--wide">
            Standard pricing varies by market based on rental minimums, delivery rates,
            Complimentary Delivery Thresholds, and typical travel requirements.
          </p>

          <div
            id="guidelines-markets-panel"
            className="guidelines-markets-panel"
            hidden={!marketsOpen}
          >
            <div className="guidelines-market-list" role="list">
              {MARKET_BANDS.map((band) => {
                const isOpen = openMarketIds.has(band.id);
                const panelId = `guidelines-market-${band.id}`;
                return (
                  <div key={band.id} className="guidelines-market-row" role="listitem">
                    <button
                      type="button"
                      className="guidelines-market-toggle"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleMarket(band.id)}
                    >
                      <span className="guidelines-market-toggle-main">
                        <span className="guidelines-market-name">{band.name}</span>
                        <span className="guidelines-market-summary">
                          {band.meta} · {band.collapsedMinimumLabel}
                        </span>
                      </span>
                      <span className="guidelines-market-action">
                        {isOpen ? 'Hide Details ↑' : 'View Details ↓'}
                      </span>
                    </button>

                    <div id={panelId} className="guidelines-market-detail" hidden={!isOpen}>
                      <dl className="guidelines-market-detail-grid">
                        <div>
                          <dt>Rental Minimum</dt>
                          <dd>{band.rentalMinimum}</dd>
                        </div>
                        <div>
                          <dt>{band.isCustom ? 'Delivery Pricing' : 'Standard Delivery'}</dt>
                          <dd>
                            {band.isCustom ? (
                              <span className="guidelines-cell-primary">{band.rate}</span>
                            ) : (
                              <>
                                <span className="guidelines-cell-primary">{band.rate}</span>
                                {band.minimum && (
                                  <span className="guidelines-cell-secondary">{band.minimum}</span>
                                )}
                              </>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt>Complimentary Delivery Threshold</dt>
                          <dd>{band.complimentary}</dd>
                        </div>
                        <div>
                          <dt>Timing / Travel</dt>
                          <dd>{band.timing}</dd>
                        </div>
                        {band.samples && (
                          <div className="guidelines-market-detail-span">
                            <dt>Example Markets</dt>
                            <dd className="guidelines-market-detail-muted">{band.samples}</dd>
                          </div>
                        )}
                        {band.customPricingDetail && (
                          <div className="guidelines-market-detail-span">
                            <dt>How Custom Pricing Works</dt>
                            <dd className="guidelines-market-detail-muted">
                              {band.customPricingDetail}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="guidelines-market-global-note">
              {MULTI_DAY_COMPLIMENTARY_NOTE}
            </p>
            <p className="guidelines-note">
              Note: Rental minimums are calculated post-discount and pre-tax.
            </p>
          </div>
        </section>

        <div className="guidelines-split">
          <section className="guidelines-section guidelines-section--split" aria-labelledby="guidelines-nonstandard">
            <h3 id="guidelines-nonstandard" className="guidelines-label">
              Non-Standard Delivery
            </h3>
            <p className="guidelines-text">
              Additional fees may apply when a project requires more time, labor, travel, or
              resources than a standard delivery.
            </p>
            <ul className="guidelines-list">
              {NON_STANDARD_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="guidelines-scope-note">
              <strong>Project scope changed?</strong> Recalculate the delivery fee if changes
              affect trucks, crew, timing, travel, access, or overall delivery requirements
              before sending an updated proposal to the client.
            </p>
          </section>

          <section className="guidelines-section guidelines-section--split" aria-labelledby="guidelines-approvals">
            <h3 id="guidelines-approvals" className="guidelines-label">
              Approvals
            </h3>
            <p className="guidelines-text">
              When pricing falls outside the standard structure or requires an exception, confirm
              approval before communicating the fee to the client.
            </p>
            <div className="guidelines-approvals-wrap">
              <table className="guidelines-table guidelines-table--approvals">
                <thead>
                  <tr>
                    <th scope="col">Item</th>
                    <th scope="col">Approver</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Non-standard delivery pricing or exceptions</td>
                    <td>JP</td>
                  </tr>
                  <tr>
                    <td>Rental minimum exceptions</td>
                    <td>NP</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default DeliveryGuidelines;
